import { Navigate } from "react-router-dom";
import {
  furthestReachableStep,
  furthestSubFlowIndex,
  subFlowIndex,
  submittingStep,
  isStepSatisfied,
  ONBOARDING_BASE_PATH,
} from "./steps/registry";
import { useFlowState } from "./state/FlowProvider";

/**
 * Stops a step rendering before the answers it depends on exist.
 *
 * This is not only about someone pasting a URL. Browser back and forward, a
 * bookmark, a refresh after the flow was reset, and a link from a stale email
 * all land on a step directly. Without a guard, the profile screen renders for
 * someone with no account and submits into a void.
 *
 * The redirect target is the furthest step the flow may currently enter, so the
 * member lands where they actually left off rather than being thrown back to
 * the start.
 *
 * It also closes the flow behind a completion that has been sent. The layout
 * gives the confirmation no back control, but the browser's own Back is a
 * navigation like any other, and the answers behind it are already with the
 * server. Mobile needs no equivalent: it resets the navigation stack, which web
 * history cannot do (ECH-439).
 *
 * @param {object} props
 * @param {import('./steps/registry').StepDefinition} props.step
 * @param {string} [props.goal] the goal sub-flow this route renders, for the
 *   five routes that share the `person_flow` step. Known statically from
 *   `routes.jsx`, so the guard never has to read it out of the URL.
 * @param {import('react').ReactNode} props.children
 */
const StepGuard = ({ step, goal, children }) => {
  const state = useFlowState();

  // Sent already, so there is nothing here left to change. Falls through when
  // the confirmation itself cannot be entered, rather than redirecting to a
  // step that would redirect straight back out.
  if (!step.submitsOnArrival && state.submitted) {
    const sent = submittingStep(state);
    if (sent) {
      return <Navigate to={`${ONBOARDING_BASE_PATH}/${sent.path}`} replace />;
    }
  }

  if (isStepSatisfied(step, state)) {
    const target = furthestReachableStep(state);
    return <Navigate to={`${ONBOARDING_BASE_PATH}/${target.path}`} replace />;
  }

  if (!step.isReachable(state)) {
    // `replace` so the unreachable URL does not enter history — otherwise
    // pressing back returns to it and bounces again, trapping the member.
    const target = furthestReachableStep(state);
    return <Navigate to={`${ONBOARDING_BASE_PATH}/${target.path}`} replace />;
  }

  if (goal) {
    // The sub-flows share one step, so reachability alone cannot tell a valid
    // one from a goal that was never selected — nor from one further ahead than
    // the member has actually got to. Selecting `build_skills` does not entitle
    // you to skip the four sub-flows in front of it, which is the same problem
    // `furthestReachableStep` solves for steps and needs the same answer.
    const index = subFlowIndex(state.goals, goal);
    const furthest = furthestSubFlowIndex(state.goals, state.goalData);

    if (index === -1 || index > furthest) {
      // Not an unreachable step: the member belongs in `person_flow`, just not
      // on this sub-flow. The step's index route decides which one is right.
      return <Navigate to={`${ONBOARDING_BASE_PATH}/${step.path}`} replace />;
    }
  }

  return children;
};

export default StepGuard;
